import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useQBEstimates, QBEstimate } from "@/hooks/useQBEstimates";
import { useEstimates, Estimate } from "@/hooks/useEstimates";
import { NewProjectWizard, WizardEstimate } from "@/components/controller/NewProjectWizard";
import { NewEstimateDialog } from "@/components/controller/NewEstimateDialog";
import { ConvertEstimateDialog } from "@/components/controller/ConvertEstimateDialog";
import { DuplicateEstimateDialog } from "@/components/controller/DuplicateEstimateDialog";
import {
  EstimateDetailDrawer,
  unifyEstimate,
  unifyQBEstimate,
} from "@/components/controller/EstimateDetailDrawer";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, ArrowRight, Eye, Send, Copy } from "lucide-react";
import { RequirePermission } from "@/components/RequirePermission";
import { useIsMobile } from "@/hooks/use-mobile";
import { PageActions } from "@/components/shared/PageActions";

const fmt = (n: number | null | undefined) =>
  n != null ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

const safeDate = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

const statusClass: Record<string, string> = {
  Draft: "bg-muted text-muted-foreground",
  Sent: "bg-blue-100 text-blue-800",
  Accepted: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-destructive/15 text-destructive",
  Closed: "bg-muted text-muted-foreground",
  Converted: "bg-violet-100 text-violet-800",
  Pending: "bg-yellow-100 text-yellow-800",
};

const syncDot: Record<string, string> = {
  synced: "bg-emerald-500", pending: "bg-yellow-500", error: "bg-destructive", not_synced: "bg-muted-foreground",
};

type SourceFilter = "all" | "cmdx" | "qb";

interface UnifiedRow {
  source: "cmdx" | "qb";
  id: string;
  number: string | null;
  customerName: string | null;
  date: string | null;
  amount: number | null;
  status: string;
  syncStatus?: string | null;
  jobType?: string | null;
  sentAt?: string | null;
  viewedAt?: string | null;
  viewCount?: number;
  raw: Estimate | QBEstimate;
}

const JOB_TYPE_LABELS: Record<string, string> = {
  flooring: "FL", roofing: "RFN", emergency: "ER", general: "GC", other: "OTH",
};

const JOB_TYPE_CLASS: Record<string, string> = {
  flooring: "bg-blue-100 text-blue-800", roofing: "bg-orange-100 text-orange-800",
  emergency: "bg-red-100 text-red-800", general: "bg-muted text-muted-foreground", other: "bg-muted text-muted-foreground",
};

const EstimatesInner = () => {
  const navigate = useTabNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { data: cmdxEstimates, isLoading: cmdxLoading } = useEstimates();
  const { data: qbEstimates, isLoading: qbLoading } = useQBEstimates();

  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [newOpen, setNewOpen] = useState(false);
  const [editEstimate, setEditEstimate] = useState<Estimate | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertEstimate, setConvertEstimate] = useState<any>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEstimate, setWizardEstimate] = useState<WizardEstimate | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<UnifiedRow | null>(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateEstimate, setDuplicateEstimate] = useState<Estimate | null>(null);

  // Handle edit from detail page navigation
  useEffect(() => {
    const state = location.state as { editEstimate?: Estimate } | null;
    if (state?.editEstimate) {
      setEditEstimate(state.editEstimate);
      setNewOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const rows = useMemo<UnifiedRow[]>(() => {
    const result: UnifiedRow[] = [];
    if (sourceFilter !== "qb" && cmdxEstimates) {
      cmdxEstimates.forEach((e) => {
        const raw = e as any;
        result.push({
          source: "cmdx", id: e.id, number: e.estimate_number,
          customerName: e.customers?.name ?? null, date: e.txn_date,
          amount: e.total_amount, status: e.status,
          syncStatus: e.qb_sync_status, jobType: raw.job_type ?? null,
          sentAt: raw.sent_at, viewedAt: raw.viewed_at, viewCount: raw.view_count ?? 0,
          raw: e,
        });
      });
    }
    if (sourceFilter !== "cmdx" && qbEstimates) {
      qbEstimates.forEach((e) => {
        const st = e.txn_status || e.status || "Pending";
        result.push({
          source: "qb", id: e.id, number: e.doc_number,
          customerName: e.customer_ref_name || e.customer_qb_id,
          date: e.txn_date, amount: e.total_amount, status: st, raw: e,
        });
      });
    }
    return result;
  }, [cmdxEstimates, qbEstimates, sourceFilter]);

  const handleRowClick = (row: UnifiedRow) => {
    if (row.source === "cmdx") {
      navigate(`/controller/estimates/${row.id}`);
    } else {
      setSelectedRow(row);
      setDetailOpen(true);
    }
  };

  const buildConvertData = (row: UnifiedRow) => {
    if (row.source === "cmdx") {
      const e = row.raw as Estimate;
      const raw = e as any;
      return {
        id: e.id,
        project_id: raw.project_id ?? null,
        is_change_order: raw.is_change_order ?? false,
        customer_id: raw.customer_id ?? null,
        customer_name: e.customers?.name ?? null,
        total_amount: e.total_amount,
        line_items: e.line_items as any[],
        estimate_number: e.estimate_number,
      };
    }
    const e = row.raw as QBEstimate;
    return {
      id: e.id,
      total_amount: e.total_amount,
      line_items: (e.line_items as any[]) ?? [],
      estimate_number: e.doc_number,
    };
  };

  const handleConvert = (row: UnifiedRow) => {
    setConvertEstimate(buildConvertData(row));
    setDetailOpen(false);
    setConvertOpen(true);
  };

  const handleEdit = (row: UnifiedRow) => {
    if (row.source !== "cmdx") return;
    setEditEstimate(row.raw as Estimate);
    setDetailOpen(false);
  };

  const handleDuplicate = (row: UnifiedRow) => {
    if (row.source !== "cmdx") return;
    setDuplicateEstimate(row.raw as Estimate);
    setDetailOpen(false);
    setDuplicateOpen(true);
  };

  const canConvert = (row: UnifiedRow) =>
    (row.source === "cmdx" && row.status === "Accepted") ||
    (row.source === "qb" && ["Accepted", "Closed"].includes(row.status));

  const loading = cmdxLoading || qbLoading;

  const detailEstimate = selectedRow
    ? selectedRow.source === "cmdx" ? unifyEstimate(selectedRow.raw as Estimate) : unifyQBEstimate(selectedRow.raw as QBEstimate)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight">Estimates</h1>
          <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as SourceFilter)}>
            <SelectTrigger className="w-32 md:w-40 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Source: All</SelectItem>
              <SelectItem value="cmdx">CommandX</SelectItem>
              <SelectItem value="qb">QuickBooks</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PageActions actions={[
          { label: "New Estimate", icon: Plus, onClick: () => setNewOpen(true) },
        ]} />
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : !rows.length ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-lg font-medium">No estimates yet</p>
          <p className="text-sm mt-1">Create your first estimate to get started</p>
          <Button className="mt-4" onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Estimate
          </Button>
        </div>
      ) : isMobile ? (
        /* ── Mobile Card View ── */
        <div className="space-y-3">
          {rows.map((row) => (
            <Card key={`${row.source}-${row.id}`} className="cursor-pointer active:bg-muted/50" onClick={() => handleRowClick(row)}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {row.source === "qb" && <Badge variant="outline" className="bg-muted text-muted-foreground text-[10px] font-mono px-1.5 py-0">QB</Badge>}
                    <span className="font-mono text-sm font-medium">{row.number ?? "—"}</span>
                    {row.jobType && JOB_TYPE_LABELS[row.jobType] && (
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${JOB_TYPE_CLASS[row.jobType] ?? ""}`}>
                        {JOB_TYPE_LABELS[row.jobType]}
                      </Badge>
                    )}
                  </div>
                  <Badge variant="outline" className={statusClass[row.status] ?? "bg-muted text-muted-foreground"}>
                    {row.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground truncate max-w-[60%]">{row.customerName ?? "—"}</span>
                  <span className="text-lg font-bold">{fmt(row.amount)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{safeDate(row.date)}</span>
                  <div className="flex items-center gap-2">
                    {row.sentAt && <span className="flex items-center gap-1"><Send className="h-3 w-3" />{safeDate(row.sentAt)}</span>}
                    {row.viewedAt && row.status === "Sent" && <Eye className="h-3 w-3 text-blue-500" />}
                  </div>
                </div>
                <div className="flex gap-2 mt-1">
                  {canConvert(row) && (
                    <Button variant="outline" size="sm" className="flex-1" onClick={(e) => { e.stopPropagation(); handleConvert(row); }}>
                      Convert <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  )}
                  {row.source === "cmdx" && (
                    <Button variant="outline" size="sm" className="flex-1" onClick={(e) => { e.stopPropagation(); handleDuplicate(row); }}>
                      <Copy className="mr-1 h-3 w-3" /> Duplicate
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* ── Desktop Table ── */
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Est #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              {sourceFilter !== "qb" && <TableHead>QB Sync</TableHead>}
              <TableHead className="w-44" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${row.source}-${row.id}`} className="cursor-pointer hover:bg-muted/50" onClick={() => handleRowClick(row)}>
                <TableCell>
                  {row.source === "qb" ? (
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="bg-muted text-muted-foreground text-xs font-mono px-1.5 py-0">QB</Badge>
                      <span className="font-mono text-sm">{row.number ?? "—"}</span>
                    </div>
                  ) : (
                    <Badge className="bg-primary text-primary-foreground text-xs font-mono">{row.number ?? "—"}</Badge>
                  )}
                  {row.jobType && JOB_TYPE_LABELS[row.jobType] && (
                    <Badge variant="outline" className={`ml-1 text-[10px] px-1.5 py-0 ${JOB_TYPE_CLASS[row.jobType] ?? ""}`}>
                      {JOB_TYPE_LABELS[row.jobType]}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{row.customerName ?? "—"}</TableCell>
                <TableCell>{safeDate(row.date)}</TableCell>
                <TableCell className="text-right">{fmt(row.amount)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className={statusClass[row.status] ?? "bg-muted text-muted-foreground"}>
                      {row.status}
                    </Badge>
                    {row.viewedAt && row.status === "Sent" && (
                      <span title={`Viewed ${row.viewCount ?? 1} time(s)`}><Eye className="h-3 w-3 text-blue-500" /></span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {row.sentAt ? (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Send className="h-3 w-3" /> {safeDate(row.sentAt)}
                    </div>
                  ) : "—"}
                </TableCell>
                {sourceFilter !== "qb" && (
                  <TableCell>
                    {row.source === "cmdx" && row.syncStatus ? (
                      <div className="flex items-center gap-1.5">
                        <span className={`h-2 w-2 rounded-full ${syncDot[row.syncStatus] ?? syncDot.not_synced}`} />
                        <span className="text-xs text-muted-foreground capitalize">{row.syncStatus}</span>
                      </div>
                    ) : row.source === "qb" ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : null}
                  </TableCell>
                )}
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1 justify-end">
                    {canConvert(row) && (
                      <Button variant="ghost" size="sm" onClick={() => handleConvert(row)}>
                        Convert → <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                    {row.source === "cmdx" && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Duplicate" onClick={() => handleDuplicate(row)}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <NewEstimateDialog
        open={newOpen}
        onOpenChange={(v) => { setNewOpen(v); if (!v) setEditEstimate(null); }}
        editEstimate={editEstimate}
      />
      <NewProjectWizard open={wizardOpen} onOpenChange={setWizardOpen} initialEstimate={wizardEstimate} />
      <ConvertEstimateDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        estimate={convertEstimate}
        onNewProject={() => {
          if (convertEstimate) {
            setWizardEstimate({
              id: convertEstimate.id,
              doc_number: convertEstimate.estimate_number ?? null,
              customer_qb_id: null,
              customer_ref_name: convertEstimate.customer_name ?? null,
              total_amount: convertEstimate.total_amount ?? null,
              line_items: convertEstimate.line_items ?? null,
            });
            setWizardOpen(true);
          }
        }}
      />
      <EstimateDetailDrawer
        estimate={detailEstimate}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onConvert={selectedRow ? () => handleConvert(selectedRow) : undefined}
        onEdit={selectedRow && selectedRow.source === "cmdx" && selectedRow.status === "Draft" ? () => handleEdit(selectedRow) : undefined}
        onDuplicate={selectedRow && selectedRow.source === "cmdx" ? () => handleDuplicate(selectedRow) : undefined}
        rawEstimate={selectedRow?.source === "cmdx" ? (selectedRow.raw as Estimate) : null}
      />
      <DuplicateEstimateDialog
        open={duplicateOpen}
        onOpenChange={(v) => { setDuplicateOpen(v); if (!v) setDuplicateEstimate(null); }}
        sourceEstimate={duplicateEstimate}
      />
    </div>
  );
};

const Estimates = () => (
  <RequirePermission anyOf={["edit_contract_sov", "view_projects"]}>
    <EstimatesInner />
  </RequirePermission>
);

export default Estimates;
